import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Route, Routes, useNavigate } from "react-router-dom";
import BreadCrumbs from "../../Components/BreadCrumbs/BreadCrumbs";
import Button from "../../Components/Button/Button";
import CreateSupportTicket from "../../Components/CreateSupportTicket/CreateSupportTicket";
import SupportTicketPreview from "../../Components/Previews/SupportTicketPreview";
import Table from "../../Components/Table/Table";
import { fetchSupportRequests } from "../../Redux/support/action";
import { isValidArray } from "../../Services/validators";
import { ticketPillFor } from "./supportFormat";
import {
  convertToSupportTableData,
  supportTableField,
  supportTableHeadings
} from "../../Utils/Constants/Support";

const PAGE_SIZE = 50;

// Self-service "contact support" screen — lets any authenticated admin raise
// a ticket to Byepo's support team, see their filed tickets, and reply to
// the thread. Backed entirely by the /support/** Cloud Function wrapper
// around Plane, never Firestore. Mirrors Screens/Reports/Reports.js's own
// layout (BreadCrumbs + top-right action button + Table) for visual
// consistency with the rest of the admin panel.
function Support(props) {
  const navigate = useNavigate();
  const requests = props.requests;
  const [sort, setSort] = useState({ title: "FILED AT", ascending: false });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!requests.loaded && !requests.loading) {
      fetchSupportRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedItems = useMemo(() => {
    const field = supportTableField(sort.title);
    // createdAt/statusLabel don't exist as plain fields on the raw ticket —
    // sort on their derived value directly rather than sorting after
    // convertToSupportTableData formats createdAt into a display string
    // (which would sort lexicographically instead of chronologically).
    const sortValue = (ticket) => {
      if (field === "createdAt") return new Date(ticket.createdAt);
      if (field === "statusLabel") return ticketPillFor(ticket, null).label;
      return ticket[field];
    };
    return [...requests.items].sort((a, b) => {
      const left = sortValue(a);
      const right = sortValue(b);
      if (left < right) return sort.ascending ? -1 : 1;
      if (left > right) return sort.ascending ? 1 : -1;
      return 0;
    });
  }, [requests.items, sort]);

  const tableData = useMemo(() => convertToSupportTableData(sortedItems), [
    sortedItems
  ]);
  const totalPage = Math.ceil(tableData.length / PAGE_SIZE) || 1;
  const pageData = tableData.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const openTicket = (row) => navigate(`/support/${row.workItemId}`);

  return (
    <>
      <Routes>
        <Route
          path="new"
          element={<CreateSupportTicket isMobileView={props.isMobileView} />}
        />
        <Route
          path=":workItemId"
          element={<SupportTicketPreview isMobileView={props.isMobileView} />}
        />
      </Routes>
      <div className="inherit-parent-height inherit-parent-width overflow-hidden position-relative">
        <div className="display-flex flex-justify-content-space-between flex-align-items-center">
          <BreadCrumbs />
          <div>
            <Button
              className="padding-horizontal-large "
              text="Raise a ticket"
              onClick={() => navigate("/support/new")}
              data-cy="RAISE-SUPPORT-TICKET"
            />
          </div>
        </div>

        <div
          className="padding-top-larger"
          style={{ height: "calc(100% - 48px)" }}
        >
          {requests.loading && !requests.loaded ? (
            <SupportTableSkeleton />
          ) : requests.loaded && !isValidArray(requests.items) ? (
            <SupportEmptyState onRaiseTicket={() => navigate("/support/new")} />
          ) : (
            <Table
              title="MY TICKETS"
              allowCollapse={false}
              collapse={true}
              headers={supportTableHeadings}
              onClick={openTicket}
              data={pageData}
              getField={supportTableField}
              totalPage={totalPage}
              currentPage={currentPage}
              previousPageOnClick={() => setCurrentPage((page) => page - 1)}
              nextPageOnClick={() => setCurrentPage((page) => page + 1)}
              sort={sort}
              handleSort={(title) => {
                setCurrentPage(1);
                setSort({
                  title,
                  ascending: sort.title === title ? !sort.ascending : true
                });
              }}
              backgroundColor="background-color-primary"
            />
          )}
        </div>
      </div>
    </>
  );
}

// Shown while the initial fetch is in flight — Table's own "no results"
// fallback would otherwise flash briefly during this window since `data`
// starts out empty before it's known whether there really are zero tickets.
function SupportTableSkeleton() {
  const shimmerStyle =
    getComputedStyle(document.documentElement).getPropertyValue(
      "--primaryColor"
    ) === "#0E0E0E"
      ? "shimmer-dark"
      : "shimmer-light";

  return (
    <div data-cy="SUPPORT-TABLE-SKELETON">
      <div className={shimmerStyle} style={{ height: "16px", width: "120px" }} />
      <div style={{ height: "24px" }} />
      {[...Array(6)].map((_, index) => (
        <div
          key={index}
          className={shimmerStyle}
          style={{ height: "44px", marginBottom: "8px" }}
        />
      ))}
    </div>
  );
}

// Distinct from Table's own built-in "no results found — try different
// filter" fallback, which doesn't fit here: this screen has no filters to
// reset, so a literal zero-tickets state needs its own copy and a "Raise a
// ticket" call to action instead.
function SupportEmptyState({ onRaiseTicket }) {
  return (
    <div
      style={{ height: "calc(100% - 66px)" }}
      className="inherit-parent-width display-flex flex-direction-column flex-align-items-center flex-justify-content-center"
      data-cy="NO-SUPPORT-TICKETS-FALLBACK"
    >
      <div className="inherit-parent-width white-space-nowrap font-color-tertiaryColor font-size-larger flex-justify-content-center">
        No support tickets yet
      </div>
      <div className="white-space-nowrap font-size-small padding-top-default font-color-tertiaryColor6">
        RAISE A TICKET AND WE'LL GET BACK TO YOU
      </div>
      <div style={{ width: "150px" }} className="padding-top-medium">
        <Button
          boxShadow={false}
          onClick={onRaiseTicket}
          text="Raise a ticket"
          data-cy="EMPTY-STATE-RAISE-TICKET"
        />
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  requests: state.support.requests
});

export default connect(mapStateToProps)(Support);
